import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription }                                                                 from 'rxjs';
import { TranslateService }                                                             from '@ngx-translate/core';

import { IntelligenceRangeModel } from './typings/intelligence.range';
import { IntelligenceService }    from './intelligence.service';
import { TabModel }               from '../shared/components/tab-bar/typings/tab';

// display order
const PRESETS = [ 'TODAY', 'THIS_WEEK', 'LAST_WEEK', 'LAST_30', 'THIS_MONTH', 'LAST_MONTH', 'YTD', 'CUSTOM' ];

interface PreviousSelection {
  end   : string;
  preset: string;
  start : string;
}

@Component({
  selector   : 'wor-intelligence-date-range-picker',
  template: require('./intelligence.date-range-picker.component.html')
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  @Output() rangeChange = new EventEmitter<IntelligenceRangeModel>();

  public activePreset : string  = 'LAST_30';
  public customEnd    : string  = '';
  public customStart  : string  = '';
  public isEditorOpen : boolean = false;
  public previous     : PreviousSelection = null;

  public presets : Array<TabModel> = [];

  private returnPreset : string = '';
  private subscription : Subscription;

  constructor (
    private elementRef         : ElementRef,
    private intelligenceService: IntelligenceService,
    private translateService   : TranslateService
  ) {
    this.presets = PRESETS.map(preset => ({
      label: this.translateService.instant(`INTELLIGENCE_SPACE.RANGE.${ preset }`),
      value: preset
    }));
  }

  ngOnInit () : void {
    this.customEnd = this.intelligenceService.range.end_date;
    this.customStart = this.intelligenceService.range.start_date;

    this.subscription = this.intelligenceService.rangeChanged.subscribe(range => {
      if ( range.start_date === this.customStart && range.end_date === this.customEnd ) {
        return;
      }

      if ( this.previous && range.start_date === this.previous.start && range.end_date === this.previous.end ) {
        this.activePreset = this.previous.preset;
        this.customEnd = this.previous.end;
        this.customStart = this.previous.start;
        this.previous = null;

        return;
      }

      if ( !this.previous ) {
        this.previous = {
          end   : this.customEnd,
          preset: this.activePreset,
          start : this.customStart
        };
      }

      this.activePreset = 'CUSTOM';
      this.customEnd = range.end_date;
      this.customStart = range.start_date;
    });
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  applyCustom () : void {
    if ( !this.customStart || !this.customEnd || this.customStart > this.customEnd ) {
      return;
    }

    this.isEditorOpen = false;
    this.previous = null;
    this.returnPreset = '';

    this.rangeChange.emit({
      end_date  : this.customEnd,
      start_date: this.customStart
    });
  }

  // clicking away from an open editor puts the strip back on the preset it was on
  @HostListener('document:click', [ '$event' ])
  onDocumentClick ( event : MouseEvent ) : void {
    const target = event.target as Node;

    if ( !this.isEditorOpen || !target.isConnected || this.elementRef.nativeElement.contains(target) ) {
      return;
    }

    this.dismissCustom();
  }

  selectPreset ( preset : string ) : void {
    // custom opens the editor without committing a range
    if ( preset === 'CUSTOM' ) {
      if ( this.activePreset !== 'CUSTOM' ) {
        this.returnPreset = this.activePreset;
      }

      this.activePreset = 'CUSTOM';
      this.isEditorOpen = true;

      return;
    }

    this.activePreset = preset;
    this.isEditorOpen = false;
    this.previous = null;
    this.returnPreset = '';

    const range = this.intelligenceService.presetRange(preset);

    this.customEnd = range.end_date;
    this.customStart = range.start_date;

    this.rangeChange.emit(range);
  }

  private dismissCustom () : void {
    this.isEditorOpen = false;

    if ( this.returnPreset && this.returnPreset !== 'CUSTOM' ) {
      this.activePreset = this.returnPreset;
      this.customEnd = this.intelligenceService.range.end_date;
      this.customStart = this.intelligenceService.range.start_date;
      this.returnPreset = '';
    }
  }
}
